import { useEffect } from 'react';
import { Loader2, PartyPopper, Home } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { getHomePageUrl, getReviewQuestionUrl } from '../../utils/urlHelpers';
import { RESULT_STATUS, STUDENTASSIGNMENT_STATUS } from '../../constants';
import { useParams } from '../../hooks';
import { useToast } from '../../hooks/useToast';
import LoggedInUserWrapper from '../core/LoggedInUserWrapper';
import AssignmentResult from '../AssignmentResult/AssignmentResult';
import { Button, ElementsRenderer } from '../shared';
import { Card } from '../ui/card';
import EndReasonBanner from './EndReasonBanner';

const AssignmentEndScreen = (props) => {
  const {
    status,
    thankYouScreen,
    name,
    endButtonText,
    firstQuestionId,
    revealSolutions,
    endReasonCode,
    result,
    openResult = false
  } = props;
  const navigate = useNavigate();
  const toast = useToast();
  const { studentAssignmentId } = useParams();

  useEffect(() => {
    if (status && status !== STUDENTASSIGNMENT_STATUS.COMPLETED) {
      toast({
        title: 'Invalid URL',
        description: 'Page not found!',
        status: 'error',
        duration: 2000,
        isClosable: true
      });
      navigate(getHomePageUrl());
    }
  }, [navigate, status, toast]);

  const endText = endButtonText || 'See Result';

  const reviewFirstQuestion = () => {
    navigate(getReviewQuestionUrl({ studentAssignmentId, questionId: firstQuestionId }));
  };

  const goHome = () => {
    navigate(getHomePageUrl());
  };

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate('/activity');
  };

  return (
    <LoggedInUserWrapper
      headerProps={{
        pageTitle: name,
        showBackButton: true,
        onBack: handleBack
      }}
    >
      <div className="cw-page flex flex-1 flex-col items-center py-8">
        <div className="mb-8 flex flex-col items-center text-center">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10">
            <PartyPopper className="h-8 w-8 text-primary" aria-hidden />
          </div>
          <h1 className="text-2xl font-bold text-foreground">Assignment Complete!</h1>
          <p className="mt-1 text-muted-foreground">Great job finishing your test</p>
        </div>

        {endReasonCode && <EndReasonBanner reasonCode={endReasonCode} />}

        <div className="w-full max-w-2xl">
          <ElementsRenderer elements={thankYouScreen?.elements} />
        </div>

        {revealSolutions ? (
          <div className="mt-8 flex flex-col items-center">
            {result && result.status === RESULT_STATUS.COMPLETED ? (
              <AssignmentResult
                buttonText={endText}
                revealSolutions={revealSolutions}
                result={result}
                reviewFirstQuestion={reviewFirstQuestion}
                onGoHome={goHome}
                defaultOpen={openResult}
              />
            ) : (
              <div className="flex flex-col items-center gap-3 py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden />
                <p className="text-sm text-muted-foreground">
                  Please wait while we process your results...
                </p>
              </div>
            )}
          </div>
        ) : (
          <Card className="mt-8 border-border/60 px-8 py-6 text-center shadow-sm">
            <p className="text-lg font-semibold text-foreground">Thank you!</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Your responses have been submitted successfully.
            </p>
          </Card>
        )}

        <Button
          buttonType="secondary"
          onClick={goHome}
          className="mt-6 min-w-[200px]"
        >
          <Home className="h-4 w-4" />
          Go to Home
        </Button>
      </div>
    </LoggedInUserWrapper>
  );
};

export default AssignmentEndScreen;
